import { useMemo, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useOmniAuth } from '@omni/auth'
import { activeInstance } from '../instances'

// The "sample data" strip for a live pro-services tenant (Sam, 12 Oct 2026,
// ASDM): until the customer's mailbox is connected, the tenant database holds
// the preview's sample rows, each marked sample = true (EDNA-Command-Center
// db/modules/proservices/proservices.sql, scripts/tenant/load-sample-data.mjs).
// While any are there, every page carries one small line saying so. Once
// they are cleared (scripts/tenant/clear-sample-data.mjs, or the email run's
// first real load) the strip is gone and the page is exactly as before.

export function SampleDataFrame({ children }: { children: ReactNode }) {
  const { getClient } = useOmniAuth()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const db = useMemo(() => getClient('crm').schema('custom') as any, [getClient])
  const sample = useQuery<boolean>({
    queryKey: ['proservices', 'sample-rows'],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await db.from('ps_client').select('id').eq('sample', true).limit(1)
      if (error) return false
      return (data ?? []).length > 0
    },
  })
  if (!sample.data) return <>{children}</>
  return (
    <div className="flex h-full flex-col">
      <div
        role="note"
        className="shrink-0 px-4 py-1.5 text-center text-xs font-medium"
        style={{ background: activeInstance.theme.accentMuted ?? '#F4F4F5', color: '#3F3F46', borderBottom: '1px solid rgba(0,0,0,0.06)' }}
      >
        Sample data until your mailbox is connected.
      </div>
      <div className="min-h-0 flex-1">{children}</div>
    </div>
  )
}
